import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import config from "../config";
import CustomerLogin from "./CustomerLogin";
import BackButton from "./BackButton";

const Cart = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // State initialization with localStorage fallback
  const [cartItems, setCartItems] = useState(() => {
    return (
      location.state?.cart ||
      JSON.parse(localStorage.getItem("currentCart")) ||
      []
    );
  });

  const restaurant_id =
    location.state?.restaurant_id ||
    localStorage.getItem("current_restaurant_id");

  const [isScrolled, setIsScrolled] = useState(false);
  const [showRestaurantDetails, setShowRestaurantDetails] = useState(false);
  const [loginFormData, setLoginFormData] = useState({});
  const [restaurantDetail, setRestaurantDetail] = useState(() => {
    return JSON.parse(localStorage.getItem("current_restaurant_details")) || {};
  });
  const [isLoginVisible, setLoginVisible] = useState(false);
  const [userData, setUserData] = useState(() => {
    const storedData = localStorage.getItem("customerData");
    return storedData && storedData !== "undefined"
      ? JSON.parse(storedData)
      : null;
  });
  const [showDropdown, setShowDropdown] = useState(false);

  // Add state to track failed images
  const [failedImages, setFailedImages] = useState(new Set());

  // Responsive breakpoint state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle scroll for sticky header
  useEffect(() => {
    const handleScroll = () => {
      const headerHeight = 100;
      setIsScrolled(window.scrollY > headerHeight);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Save cart and restaurant data to localStorage
  useEffect(() => {
    if (cartItems) {
      localStorage.setItem("currentCart", JSON.stringify(cartItems));
    }
    if (restaurant_id) {
      localStorage.setItem("current_restaurant_id", restaurant_id);
    }
  }, [cartItems, restaurant_id]);

  // Fetch restaurant details
  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const storedRestaurantDetails = localStorage.getItem(
          "current_restaurant_details"
        );
        if (storedRestaurantDetails) {
          setRestaurantDetail(JSON.parse(storedRestaurantDetails));
        } else if (restaurant_id) {
          const response = await axios.get(
            `${config.API_BASE_URL}/customer/display-menu/${restaurant_id}`
          );
          const allItems = response.data;
          setRestaurantDetail(allItems.restaurant);
          localStorage.setItem(
            "current_restaurant_details",
            JSON.stringify(allItems.restaurant)
          );
        }
      } catch (error) {
        console.log("Error fetching restaurant details:", error);
      }
    };
    fetchMenu();
  }, [restaurant_id]);

  // Cart item manipulation functions
  const removeItem = (itemId) => {
    const updatedCart = cartItems
      .map((item) => {
        if (item.menu_id === itemId) {
          if (item.quantity > 1) {
            return { ...item, quantity: item.quantity - 1 };
          }
          return null;
        }
        return item;
      })
      .filter(Boolean);
    setCartItems(updatedCart);
  };

  const addItem = (itemId) => {
    const updatedCart = cartItems.map((item) => {
      if (item.menu_id === itemId) {
        return { ...item, quantity: item.quantity + 1 };
      }
      return item;
    });
    setCartItems(updatedCart);
  };

  const removeItemCompletely = (itemId) => {
    const updatedCart = cartItems.filter((item) => item.menu_id !== itemId);
    setCartItems(updatedCart);
  };

  const calculateTotal = () => {
    return cartItems.reduce(
      (total, item) => total + item.price * item.quantity,
      0
    );
  };

  const calculateItemCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      alert("Your cart is empty. Please add items before checkout.");
      return;
    }

    navigate("/customer_details", {
      state: { cart: cartItems, restaurant_id },
    });
  };

  // User authentication functions
  const handleLoginClick = () => setLoginVisible(true);
  const handleCloseLogin = () => setLoginVisible(false);
  const handleLoginSuccess = (data) => {
    setUserData(data);
    setLoginVisible(false);
  };

  // Restaurant details handlers
  const toggleRestaurantDetails = () => {
    setShowRestaurantDetails(!showRestaurantDetails);
  };

  // Image error handler to prevent infinite loops
  const handleImageError = (itemId) => {
    setFailedImages((prev) => new Set([...prev, itemId]));
  };

  // Function to get image source
  const getImageSrc = (item) => {
    if (failedImages.has(item.menu_id)) {
      return null; // Don't show any image if it failed
    }
    return item.image || null;
  };

  // Paths where login button should be shown
  const showLoginButtonPaths = ["/menu-display", "/cart", "/customer_details"];

  return (
    <div className="max-w-7xl mx-auto p-4 pb-24 md:pb-4 md:mt-5">
      <BackButton />

      {/* Restaurant Header - Sticky on scroll */}
      <div className="max-w-7xl mx-auto px-4 mt-18 md:mt-15">
        <div className="max-w-6xl mx-auto relative">
          <div
            className={`${
              isScrolled ? "fixed top-0 shadow-lg" : "relative shadow-sm"
            } left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100 transition-all duration-300`}
            onMouseEnter={
              !isMobile ? () => setShowRestaurantDetails(true) : undefined
            }
            onMouseLeave={
              !isMobile ? () => setShowRestaurantDetails(false) : undefined
            }
            onClick={isMobile ? toggleRestaurantDetails : undefined}
          >
            <div className="max-w-7xl mx-auto p-4">
              <div className="flex flex-col gap-3">
                {/* Top row with restaurant name and login */}
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    {restaurantDetail.logo && (
                      <img 
                        src={restaurantDetail.logo} 
                        alt={restaurantDetail.name}
                        className="w-10 h-10 rounded-full object-cover border border-gray-200"
                      />
                    )}
                    <h1 className="text-2xl font-bold text-gray-900 truncate max-w-[60%]">
                      {restaurantDetail.name || "Restaurant"}
                    </h1>
                  </div>

                  {showLoginButtonPaths.includes(location.pathname) &&
                    (userData ? (
                      <div className="relative">
                        <button
                          onClick={() => setShowDropdown(!showDropdown)}
                          className="flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                        >
                          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                            {userData.customer_name?.charAt(0).toUpperCase()}
                          </div>
                          <span className="text-sm font-medium text-gray-700 truncate max-w-[100px]">
                            {userData.customer_name}
                          </span>
                          <svg
                            className="w-4 h-4 text-gray-500 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                        {showDropdown && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                            <button
                              onClick={() => {
                                navigate("/customer_orders", {
                                  state: {
                                    customerId: userData?.customer_id,
                                  },
                                });
                                setShowDropdown(false);
                              }}
                              className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                              </svg>
                              Order Status
                            </button>
                            <button
                              onClick={() => {
                                localStorage.removeItem("customerData");
                                setUserData(null);
                                setShowDropdown(false);
                              }}
                              className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                              </svg>
                              Logout
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={handleLoginClick}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Login
                      </button>
                    ))}
                </div>

                {/* Restaurant details - shown on hover/click */}
                <div
                  className={`text-sm text-gray-600 overflow-x-auto transition-all duration-300 ${
                    showRestaurantDetails ? "block opacity-100" : "hidden opacity-0"
                  }`}
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    {restaurantDetail?.address ? (
                      <>
                        <div className="flex items-center gap-1">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="truncate">
                            {restaurantDetail.address}
                          </span>
                        </div>
                        <span className="text-gray-300">•</span>
                        <span className="truncate">
                          {restaurantDetail.city}
                        </span>
                        <span className="text-gray-300">•</span>
                        <span className="truncate">
                          {restaurantDetail.state}
                        </span>
                        {restaurantDetail?.phone && (
                          <>
                            <span className="text-gray-300">•</span>
                            <div className="flex items-center gap-1">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                              </svg>
                              <span>
                                {restaurantDetail.phone.replace(
                                  /(\d{3})(\d{3})(\d{4})/,
                                  "$1-$2-$3"
                                )}
                              </span>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <div className="flex items-center gap-2 text-gray-500">
                        <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                        Loading restaurant details...
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cart Content */}
      <div className="mt-6 sm:mt-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Your Cart {cartItems.length > 0 && `(${calculateItemCount()} ${calculateItemCount() === 1 ? 'item' : 'items'})`}
          </h2>
          <p className="text-gray-600">Review your order before checkout</p>
        </div>

        {cartItems.length > 0 ? (
          <div className="space-y-4 max-w-4xl mx-auto">
            {cartItems.map((item) => (
              <div
                key={item.menu_id}
                className="flex items-center bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 mr-4">
                  {getImageSrc(item) ? (
                    <img
                      src={getImageSrc(item)}
                      alt={item.item_name}
                      className="w-full h-full object-cover rounded-lg border border-gray-200"
                      onError={() => handleImageError(item.menu_id)}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg flex items-center justify-center border border-gray-200">
                      <svg
                        className="w-8 h-8 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                  )}
                </div>

                <div className="flex-grow text-left mx-4 sm:mx-6 min-w-0">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1 truncate">
                    {item.item_name}
                  </h3>
                  <p className="text-base sm:text-lg text-blue-600 font-semibold mb-2">
                    ${item.price.toFixed(2)}
                  </p>
                  <p className="text-sm sm:text-base text-gray-800 font-medium">
                    Total: <span className="text-green-600">${(item.price * item.quantity).toFixed(2)}</span>
                  </p>
                </div>

                <div className="flex flex-col items-center space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:space-x-4">
                  <div className="flex items-center space-x-3 bg-gray-100 rounded-lg px-3 py-2">
                    <button
                      onClick={() => removeItem(item.menu_id)}
                      className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors font-bold"
                      aria-label="Decrease quantity"
                    >
                      -
                    </button>
                    <span className="text-lg font-semibold min-w-8 text-center">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => addItem(item.menu_id)}
                      className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors font-bold"
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                  <button
                    onClick={() => removeItemCompletely(item.menu_id)}
                    className="text-sm text-red-500 hover:text-red-700 transition-colors font-medium flex items-center gap-1"
                    aria-label="Remove item"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 max-w-md mx-auto">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <p className="text-gray-600 text-lg mb-6">Your cart is empty</p>
            <button
              onClick={() => navigate(-1)}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-semibold text-lg"
            >
              Back to Menu
            </button>
          </div>
        )}
      </div>

      {/* Checkout Footer - Fixed on mobile */}
      {cartItems.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl p-4 transform transition-transform">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-center sm:text-left">
                <span className="text-2xl sm:text-3xl font-bold text-gray-900">
                  ${calculateTotal().toFixed(2)}
                </span>
                <span className="block text-sm text-gray-500 mt-1">
                  Sub-total for {calculateItemCount()} {calculateItemCount() === 1 ? 'item' : 'items'} + Tax & fees
                </span>
              </div>
              <button
                onClick={handleCheckout}
                className="w-full sm:w-auto px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-lg font-semibold text-lg flex items-center justify-center gap-2"
                disabled={cartItems.length === 0}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                {isMobile
                  ? `Checkout $${calculateTotal().toFixed(2)}`
                  : "Proceed to Checkout"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Login Modal */}
      {isLoginVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto transform transition-all">
            <CustomerLogin
              onClose={handleCloseLogin}
              onLoginSuccess={handleLoginSuccess}
              loginFormData={loginFormData}
              setLoginFormData={setLoginFormData}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;